const MAX_SELLER_SEARCH_TOKENS = 80;
const MIN_SELLER_SEARCH_TOKEN_LENGTH = 2;

const normalizeSearchText = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const tokenizeSearchText = value => {
  const normalized = normalizeSearchText(value);
  return normalized
    ? normalized
        .split(/\s+/)
        .filter(token => token.length >= MIN_SELLER_SEARCH_TOKEN_LENGTH)
    : [];
};

const createPrefixTokens = token => {
  const prefixes = [];

  for (let index = MIN_SELLER_SEARCH_TOKEN_LENGTH; index <= token.length; index += 1) {
    prefixes.push(token.slice(0, index));
  }

  return prefixes;
};

const tokenizeNameSearchText = value => {
  return tokenizeSearchText(value).flatMap(createPrefixTokens);
};

const getSellerCategoryLabel = (sellerCategory, userFields = []) => {
  const sellerCategoryConfig = userFields.find(field => field?.key === 'seller_category');
  const option = sellerCategoryConfig?.enumOptions?.find(
    enumOption => enumOption.option === sellerCategory
  );
  return option?.label || sellerCategory;
};

/**
 * Creates normalized tokens used by Integration API seller search.
 * Name fields are expanded into prefix tokens, while category fields remain exact tokens.
 *
 * @param {Object} params
 * @param {string?} params.firstName seller first name
 * @param {string?} params.lastName seller last name
 * @param {string?} params.displayName seller display name
 * @param {Object?} params.publicData seller public data
 * @param {Array<Object>} params.userFields user field config
 * @returns {Array<string>} unique search tokens for publicData.seller_search_tokens
 */
export const createSellerSearchTokens = params => {
  const { firstName, lastName, displayName, publicData = {}, userFields = [] } = params || {};
  const sellerCategory =
    typeof publicData.seller_category === 'string' ? publicData.seller_category.trim() : '';
  const sellerCategoryLabel = getSellerCategoryLabel(sellerCategory, userFields);
  const nameTokenSources = [firstName, lastName, displayName];
  const categoryTokenSources = [sellerCategory, sellerCategoryLabel];
  const seen = new Set();

  return [
    ...nameTokenSources.flatMap(tokenizeNameSearchText),
    ...categoryTokenSources.flatMap(tokenizeSearchText),
  ]
    .filter(token => {
      const isUnique = !seen.has(token);
      seen.add(token);
      return isUnique;
    })
    .slice(0, MAX_SELLER_SEARCH_TOKENS);
};
